/**
 * Motion Blur — directional 1D Gaussian along an arbitrary angle.
 *
 * Models the trail left by a moving camera / subject: a Gaussian-
 * weighted average along a line of length L pixels at angle θ degrees
 * from horizontal. The kernel is a 1D Gaussian sampled along (cos θ,
 * sin θ), rasterised over [−L/2, +L/2].
 *
 * NOT a true motion-blur PSF — that's a box of length L (uniform
 * weights along the line). We use a Gaussian here because:
 *
 *   1. It matches user intent ("smooth, falls off at the ends") better
 *      than a hard box, which produces ringing artefacts on RAW data.
 *   2. The integral is the same as a box (within ε), so visually it's
 *      a soft motion trail — closer to long-exposure handheld than to
 *      idealised mechanical pan.
 *
 * If a hard box is desired in v2 a `kind: 'box' | 'gaussian'` param can
 * be added — kernel construction is the only branch that changes.
 *
 * Pipeline slot: destructive pre-kernel commit (same shape as Gaussian
 * Blur). Identity short-circuit: length ≤ 0 → no-op.
 *
 * References:
 *   • Bovik, "Handbook of Image and Video Processing" 2nd ed., Chapter
 *     7.3 — directional motion-blur kernel.
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/motion-blur
 */

import { mirrorIndex, validateRGBA } from './_util.js';

/** Slider ranges. */
export const MOTION_LENGTH_MIN = 0;
export const MOTION_LENGTH_MAX = 200;
export const MOTION_ANGLE_MIN  = 0;
export const MOTION_ANGLE_MAX  = 360;

const IDENTITY_EPS = 1e-6;

/**
 * Default Motion Blur parameters. Identity (length = 0).
 *
 * @returns {{ length: number, angleDeg: number }}
 */
export function defaultParams() {
    return { length: 0, angleDeg: 0 };
}

/**
 * Identity test.
 *
 * @param {{ length?: number }} params
 * @returns {boolean}
 */
export function isIdentityMotion(params) {
    if (!params) return true;
    const L = +params.length;
    if (!Number.isFinite(L)) return true;
    return L <= IDENTITY_EPS;
}

/**
 * Build a 1D Gaussian kernel of length `2r+1` with σ chosen so the
 * Gaussian fits inside the requested motion length L. We use σ = L/4
 * which puts ~95% of the integral inside ±L/2 — a soft trail rather
 * than a hard box.
 *
 * @param {number} length  motion length in pixels
 * @returns {Float32Array} unit-sum, symmetric
 */
function buildMotionKernel(length) {
    const r = Math.max(1, Math.ceil(length / 2));
    const sigma = Math.max(0.5, length / 4);
    const inv2s2 = 1 / (2 * sigma * sigma);
    const k = new Float32Array(2 * r + 1);
    let sum = 0;
    for (let i = -r; i <= r; i++) {
        const v = Math.exp(-i * i * inv2s2);
        k[i + r] = v;
        sum += v;
    }
    const inv = 1 / sum;
    for (let i = 0; i < k.length; i++) k[i] *= inv;
    return k;
}

/**
 * Apply directional 1D Gaussian motion blur in-place on a packed RGBA
 * Float32 buffer.
 *
 * Algorithm (per pixel):
 *   For k in [−r, r]:
 *       sx = x + k · cos(θ)
 *       sy = y + k · sin(θ)
 *       sample (sx, sy) with bilinear interpolation, accumulate
 *       weighted by kernel[k+r]
 *
 * Boundary handling: mirror-extend in both x and y via `mirrorIndex`
 * — no zero-pad halo, same as Wave 3 capture-sharpen.
 *
 * Note on cost: this is a 2D O(W·H·K) convolution because the line
 * isn't axis-aligned in general — we can't separate it into H and V
 * passes. K = 2·ceil(L/2)+1. For L=50 that's ~100 taps per pixel.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.length    motion length in pixels [0, 200]
 * @param {number} params.angleDeg  angle in degrees [0, 360)
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyMotionBlur(rgba, w, h, params) {
    validateRGBA('applyMotionBlur', rgba, w, h);
    const p = params || {};
    if (isIdentityMotion(p)) return rgba;

    const length   = Math.min(MOTION_LENGTH_MAX, Math.max(MOTION_LENGTH_MIN, +p.length));
    const angleDeg = +p.angleDeg || 0;
    const rad = (angleDeg * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);

    const kernel = buildMotionKernel(length);
    const r = (kernel.length - 1) >> 1;

    /* We need a read-only copy because we sample from the original
     * while writing the destination. Float32(w·h·4) = ~33 MB at 4K. */
    const src = new Float32Array(rgba);

    /* Per-pixel: walk the kernel along (dx, dy). Bilinear sample. */
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let aR = 0, aG = 0, aB = 0;
            for (let k = -r; k <= r; k++) {
                const sx = x + k * dx;
                const sy = y + k * dy;
                /* Bilinear sample with mirror-extend on indices. */
                const x0f = Math.floor(sx);
                const y0f = Math.floor(sy);
                const fx = sx - x0f;
                const fy = sy - y0f;
                const x0 = mirrorIndex(x0f,     w);
                const x1 = mirrorIndex(x0f + 1, w);
                const y0 = mirrorIndex(y0f,     h);
                const y1 = mirrorIndex(y0f + 1, h);
                const i00 = (y0 * w + x0) * 4;
                const i01 = (y0 * w + x1) * 4;
                const i10 = (y1 * w + x0) * 4;
                const i11 = (y1 * w + x1) * 4;
                const wkk = kernel[k + r];
                const u = 1 - fx, v = 1 - fy;
                const w00 = wkk * u * v;
                const w01 = wkk * fx * v;
                const w10 = wkk * u * fy;
                const w11 = wkk * fx * fy;
                aR += src[i00]     * w00 + src[i01]     * w01 + src[i10]     * w10 + src[i11]     * w11;
                aG += src[i00 + 1] * w00 + src[i01 + 1] * w01 + src[i10 + 1] * w10 + src[i11 + 1] * w11;
                aB += src[i00 + 2] * w00 + src[i01 + 2] * w01 + src[i10 + 2] * w10 + src[i11 + 2] * w11;
            }
            const j = (y * w + x) * 4;
            rgba[j]     = aR;
            rgba[j + 1] = aG;
            rgba[j + 2] = aB;
            /* alpha untouched. */
        }
    }
    return rgba;
}
